<template>
    <b-modal content-class="argus-glass-dialog" :visible="true" :title="`Назначить ставку · ${target.employee_name_snapshot}`" size="lg" scrollable @hide="beforeHide" @hidden="$emit('closed')">
        <p>С {{ month }}. Ставка действует дальше до отмены или уже заданной будущей замены. После сохранения начисление сотрудника обновится автоматически.</p>
        <b-spinner v-if="loading" small />
        <div v-for="row in rows" :key="row.key" class="mb-3">
            <strong>{{ row.location_class === 'base' ? 'База' : 'Объекты' }}</strong>
            <p class="small text-muted mb-2">{{ row.objects.join(', ') }}</p>
            <p v-if="row.ambiguous" class="text-muted small">В архиве разные значения. Укажите согласованную ставку.</p>
            <b-form-select v-model="drafts[row.key].policy_id" :options="policyOptions" :disabled="busy || saved" :aria-label="`Политика: ${row.location_class}`" @change="drafts[row.key].use_suggestion = false" />
            <PayrollMoneyInput v-model="drafts[row.key].amount" class="mt-2" :disabled="busy || saved" :aria-label="`Ставка: ${row.location_class}`" placeholder="Сумма ставки" @input="drafts[row.key].use_suggestion = false" />
            <small v-if="row.effective_to">До {{ row.effective_to }} включительно.</small>
        </div>
        <template #modal-footer="{ hide }">
            <div class="missing-rate-footer">
                <b-alert v-if="error" show variant="danger" class="mb-2">{{ error }}</b-alert>
                <div v-if="saved" role="status" class="text-success mb-2">Ставка сохранена, начисление сотрудника обновлено. Неполные начисления требуют дальнейшей проверки.</div>
                <div class="d-flex flex-wrap justify-content-end" style="gap: 8px">
                    <b-button :disabled="busy" @click="hide()">{{ saved ? 'Готово' : 'Отмена' }}</b-button>
                    <b-button v-if="!saved" variant="primary" :disabled="loading || busy || !rows.length" @click="save">Сохранить и пересчитать</b-button>
                </div>
            </div>
        </template>
    </b-modal>
</template>

<script>
import api from '@/utils/api';
import PayrollMoneyInput from './PayrollMoneyInput.vue';

export default {
    components: {PayrollMoneyInput},
    props: {periodId: {type: [Number, String], required: true}, target: {type: Object, required: true}},
    data: () => ({preview: null, rows: [], drafts: {}, loading: true, busy: false, error: '', saved: false, baseline: '', allowClose: false}),
    computed: {
        month() { return this.preview ? this.preview.period.starts_on.split('-').reverse().join('.') : 'выбранного месяца'; },
        policyOptions() { return [{value: null, text: 'Выберите политику'}].concat((this.preview ? this.preview.policies : []).map(policy => ({
            value: Number(policy.id), text: `${policy.name} · ${policy.currency} / ${{hourly: 'час', daily: 'день', monthly: 'месяц'}[policy.basis]}`}))); },
        dirty() { return !this.saved && !!this.baseline && this.baseline !== JSON.stringify(this.drafts); }
    },
    watch: {dirty(value) { this.$emit('dirty', value); }},
    mounted() { this.load(); },
    beforeDestroy() { this.$emit('dirty', false); },
    methods: {
        async load() {
            try {
                const {data} = await api.get(`/api/payroll/periods/${this.periodId}/rates`, {ignoreModal: true});
                this.preview = data;
                if (!data.can_manage) throw Error('Назначение ставок сейчас недоступно.');
                const locations = this.target.calculation_details.components.map(part => part.location_class);
                this.rows = data.rows.filter(row => Number(row.employee_id) === Number(this.target.employee_id)
                    && locations.includes(row.location_class) && row.status === 'missing');
                this.rows.forEach(row => {
                    const suggestion = row.suggestion && row.suggestion.policy_id ? row.suggestion : null;
                    this.$set(this.drafts, row.key, {policy_id: suggestion ? suggestion.policy_id : null,
                        amount: suggestion ? (suggestion.rate_minor / 100).toFixed(2) : '', use_suggestion: !!suggestion});
                });
                this.baseline = JSON.stringify(this.drafts);
                if (!this.rows.length) throw Error('Ставки уже изменились. Закройте форму и обновите расчёт.');
            } catch (error) { this.error = error.response && error.response.data.msg || error.message || 'Не удалось загрузить ставки.'; }
            finally { this.loading = false; }
        },
        async beforeHide(event) {
            if (this.busy) { event.preventDefault(); return; }
            if (this.allowClose || !this.dirty) return;
            event.preventDefault();
            if (await this.$bvModal.msgBoxConfirm('Закрыть форму без сохранения ставки?', {okTitle: 'Закрыть', cancelTitle: 'Остаться'})) {
                this.allowClose = true; this.$emit('closed');
            }
        },
        async save() {
            if (this.busy || this.loading || this.saved || !this.preview || !this.preview.can_manage) return;
            const rows = this.rows.map(row => {
                const draft = this.drafts[row.key], value = String(draft.amount).trim().replace(',', '.');
                const amount = /^\d+(\.\d{1,2})?$/.test(value) ? Math.round(Number(value) * 100) : null;
                return {key: row.key, policy_id: draft.policy_id, rate_minor: amount, use_suggestion: draft.use_suggestion};
            });
            if (!rows.length || rows.some(row => !row.policy_id || row.rate_minor === null || !Number.isSafeInteger(row.rate_minor))) {
                this.error = 'Выберите политику и укажите ставку для каждой строки. Ноль можно задать явно.'; return;
            }
            this.busy = true; this.error = ''; this.$emit('busy', true);
            try {
                await api.post(`/api/payroll/periods/${this.periodId}/rates/batch`, {expected_revision: this.preview.expected_revision, rows, recalculate: true}, {ignoreModal: true});
                this.saved = true; this.$emit('dirty', false); this.$emit('saved', rows.length);
            } catch (error) { this.error = error.response && error.response.data.msg || 'Ставка не сохранена. Введённые значения оставлены в форме.'; }
            finally { this.busy = false; this.$emit('busy', false); }
        }
    }
};
</script>

<style scoped>
.missing-rate-footer { width: 100%; }
</style>
